/**
 * CielHS 풍 2-column drill — 좌 patron (sibling list, 선택 highlight) + 우 detail (child).
 *
 * Drill 매트릭스:
 *   level   | patron (좌)               | detail (우)
 *   --------|--------------------------|---------------------
 *   section | 21 부 list                | placeholder "부 선택"
 *   chapter | 21 부 (highlight)         | 그 부의 류 list
 *   heading | 그 부의 류 (highlight)    | 그 류의 호 list (chapter-headings)
 *   hierarchy | 그 류의 호 (highlight)  | 그 호의 hierarchy (HeadingGrid)
 *   hsk     | 그 호의 세번 (highlight)  | HskDetailPanel
 *
 * 4 list component (SectionsList / ChaptersList / HeadingsList / HskList)
 * + drill switcher (HierarchyDrillView default export).
 */
import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  ALL_SECTION_IDS,
  SECTION_NAMES,
  SECTION_TO_CHAPTERS,
  chaptersBySectionId,
  sectionIdOfChapter,
} from "./hsHierarchyData";
import { CHAPTERS_97 } from "./chaptersData";
import HeadingGrid from "./HeadingGrid";
import HskDetailPanel from "./HskDetailPanel";
import ChapterHeadingsGrid from "./ChapterHeadingsGrid";
import SectionChaptersGrid from "./SectionChaptersGrid";

// ─── URL navigation helpers ───────────────────────────────

export function hierarchyNavigationUrl(currentHref, target) {
  const u = new URL(currentHref);
  u.searchParams.set("audit", "onto-detail");
  // 부분 소호 검색(예: 8517.51)이 0건인 뒤 왼쪽 호를 눌러도 sub가 남으면
  // 같은 빈 필터가 다시 적용된다. 계층을 새로 선택할 때는 부분 필터도 초기화한다.
  ["section", "chapter", "hs", "hsk", "sub"].forEach((k) =>
    u.searchParams.delete(k),
  );
  if (target.section) u.searchParams.set("section", String(target.section));
  if (target.chapter) u.searchParams.set("chapter", target.chapter);
  if (target.hs) u.searchParams.set("hs", target.hs);
  if (target.hsk) u.searchParams.set("hsk", target.hsk);
  return u.toString();
}

function navigate(target) {
  window.history.pushState(
    {},
    "",
    hierarchyNavigationUrl(window.location.href, target),
  );
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function formatTariffLookupCode(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}.${digits.slice(4)}`;
  return `${digits.slice(0, 4)}.${digits.slice(4, 6)}-${digits.slice(6)}`;
}

function chapterName(ch) {
  const found = CHAPTERS_97.find((c) => c.ch === ch);
  return found ? found.name : "";
}

// ─── 4 base list components ───────────────────────────────

function SectionsList({ activeSection }) {
  return (
    <ListColumn title="부">
      {ALL_SECTION_IDS.map((id) => {
        const active = String(id) === String(activeSection);
        return (
          <ListItem
            key={id}
            active={active}
            code={String(id).padStart(2, "0")}
            label={SECTION_NAMES[id]}
            onClick={() => navigate({ section: id })}
          />
        );
      })}
    </ListColumn>
  );
}

function ChaptersList({ sectionId, activeChapter }) {
  const id = sectionId ? Number(sectionId) : null;
  const chapters = chaptersBySectionId(id);
  if (!chapters) {
    return (
      <ListColumn title="류">
        <Muted>유효하지 않은 부</Muted>
      </ListColumn>
    );
  }
  return (
    <ListColumn title="류">
      {chapters.map((ch) => {
        const active =
          String(ch).padStart(2, "0") ===
          String(activeChapter).padStart(2, "0");
        return (
          <ListItem
            key={ch}
            active={active}
            code={ch}
            label={chapterName(ch) || "—"}
            onClick={() => navigate({ chapter: ch })}
          />
        );
      })}
    </ListColumn>
  );
}

function HeadingsList({ chapter, activeHs4, onSelect }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!chapter || !/^[0-9]{1,2}$/.test(chapter)) {
      setData({ headings: [] });
      setLoading(false);
      return;
    }
    const padded = String(chapter).padStart(2, "0");
    setLoading(true);
    setErr(null);
    fetch(`/api/onto/chapter-headings?chapter=${padded}&lang=kor`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setData(d || { headings: [] }))
      .catch((e) => setErr(String(e.message || e)))
      .finally(() => setLoading(false));
  }, [chapter]);

  return (
    <ListColumn title="호">
      {loading && <Muted>불러오는 중…</Muted>}
      {err && <Muted style={{ color: "#d93025" }}>오류: {err}</Muted>}
      {!loading &&
        !err &&
        (data?.headings || []).flatMap(({ hs4, name, section_label }) => {
          const items = [];
          // 절 시작 호 직전에 절 헤더 row 삽입 (ch72 의 제1절/제2절/제3절/제4절)
          if (section_label) {
            items.push(
              <SectionDivider key={`__section_${hs4}`} label={section_label} />,
            );
          }
          const active = hs4 === activeHs4;
          items.push(
            <ListItem
              key={hs4}
              active={active}
              code={hs4}
              label={name || ""}
              onClick={() =>
                onSelect ? onSelect(hs4, name) : navigate({ hs: hs4 })
              }
            />,
          );
          return items;
        })}
      {!loading && !err && (!data?.headings || data.headings.length === 0) && (
        <Muted>호 없음</Muted>
      )}
    </ListColumn>
  );
}

// 호 list 안의 절 (subsection) 헤더 row — 클릭 불가, 강조 라벨.
function SectionDivider({ label }) {
  return (
    <div
      style={{
        padding: "8px 8px 4px",
        marginTop: 4,
        fontSize: 12,
        fontWeight: 700,
        color: "var(--audit-accent)",
        borderTop: "1px solid var(--audit-border)",
        whiteSpace: "normal",
        wordBreak: "keep-all",
        overflowWrap: "anywhere", // 모바일 좁은 폭에서 긴 토큰 흘러넘침 방지
        lineHeight: 1.4,
      }}
    >
      {label}
    </div>
  );
}

function HskList({ hs4, activeHsk, rows }) {
  // hs4 는 HeadingGrid 의 source 와 같은 데이터 — heading-detail rows 활용
  if (!rows || rows.length === 0) {
    return (
      <ListColumn title="세번">
        <Muted>세번 없음</Muted>
      </ListColumn>
    );
  }
  return (
    <ListColumn title="세번">
      {rows.map((r) => {
        const active = r.hsk === activeHsk;
        // 풀 hsk 표준 dash 형식 (4 + 2 - 4) — 호 prefix 포함으로 헷갈림 방지
        // "8201100000" → "8201.10-0000" (이미지 41 CielHS 패턴)
        const code = `${r.hsk.slice(0, 4)}.${r.hsk.slice(4, 6)}-${r.hsk.slice(6)}`;
        const label = r.name_ko || r.popum || "";
        return (
          <ListItem
            key={r.hsk}
            active={active}
            code={code}
            label={label.length > 32 ? label.slice(0, 32) + "…" : label}
            onClick={() => navigate({ hs: hs4, hsk: r.hsk })}
          />
        );
      })}
    </ListColumn>
  );
}

// ─── 공용 sub-components ──────────────────────────────────

function ListColumn({ title, children }) {
  // CielHS 풍 page-flow scroll — ListColumn 자체 maxHeight 없음. 페이지 전체가
  // 한 scrollbar 로 자연스럽게 흐름. 별도 overflow 박스 없음 (이미지 28/29 패턴).
  return (
    <div
      style={{
        border: "1px solid var(--audit-border)",
        background: "var(--audit-bg-elevated)",
        borderRadius: 6,
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
      }}
    >
      <div
        style={{
          padding: "8px 12px",
          borderBottom: "1px solid var(--audit-border)",
          fontSize: 12,
          fontWeight: 700,
          color: "var(--audit-text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          background: "var(--audit-bg-subtle)",
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        {title}
      </div>
      <div
        style={{
          padding: 4,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ListItem({ active, code, label, onClick }) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = "var(--audit-bg-hover)";
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = "transparent";
      }}
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 8,
        padding: "5px 8px",
        border: "none",
        borderLeft: "3px solid transparent",
        background: active ? "var(--audit-accent-bg)" : "transparent",
        color: "var(--audit-text)",
        borderRadius: 0,
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: 12,
        lineHeight: 1.5,
        textAlign: "left",
        fontWeight: active ? 700 : 400,
        transition: "background 0.12s",
      }}
    >
      <span
        style={{
          fontFamily: "JetBrains Mono, monospace",
          fontWeight: 700,
          color: active ? "var(--audit-text)" : "var(--audit-text-muted)",
          flexShrink: 0,
          // code 길이별 dynamic — 류 2자/호 4자 = 40, hsk 12자 = 90
          minWidth: code.length > 6 ? 90 : code.length > 2 ? 50 : 28,
        }}
      >
        {code}
      </span>
      <span
        title={label}
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1,
          minWidth: 0,
        }}
      >
        {label}
      </span>
    </button>
  );
}

function Muted({ children, style }) {
  return (
    <div
      style={{
        padding: "10px 12px",
        fontSize: 12,
        color: "var(--audit-text-muted)",
        fontStyle: "italic",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function DetailPlaceholder({ children }) {
  return (
    <div
      style={{
        padding: 24,
        border: "1px dashed var(--audit-border)",
        background: "var(--audit-bg-subtle)",
        borderRadius: 6,
        color: "var(--audit-text-muted)",
        fontSize: 13,
        textAlign: "center",
      }}
    >
      {children}
    </div>
  );
}

// ─── Drill switcher (default export) ──────────────────────

export default function HierarchyDrillView({
  level,
  section,
  chapter,
  hs,
  hsk,
  sub, // 소호 필터 prefix(예 20094) — 그 소호 세번만 표시(CielHS 2009.4 검색 패턴)
  data, // heading-detail response (for hierarchy/hsk levels)
  lang,
  onRowClick,
  onHsSelect, // 셸(속견표) 내장 시 — 호 목록 클릭을 URL 이동 대신 셸 상태 갱신으로
  isMobile = false, // 모바일 — patron/detail 세로 스택 + 표 컴팩트
  searchContext, // { q, matchTokens, ... } — HeadingGrid 에 matchTokens 전달 → 매칭 row 노란 highlight
}) {
  const matchTokens = searchContext?.matchTokens || [];
  // 소호 필터 — sub prefix 로 시작하는 세번만. 호 popum 은 숨김(그 소호만 보여주기).
  const subFilteredRows =
    sub && data?.rows
      ? data.rows.filter((r) => String(r.hsk || "").startsWith(sub))
      : data?.rows || [];
  const invalidSub =
    !!sub && Array.isArray(data?.rows) && subFilteredRows.length === 0;
  // 5 단계 patron/detail 매트릭스
  let patronCol = null;
  let detailCol = null;

  // 세번 rail — 세번(hsk) 단계 좌측: 호/소호/세번 평면 목록(compact),
  //   현재 세번 하이라이트, 클릭 → 우측 상세 갱신.
  const railHs4 = data?.hs4 || (hsk ? hsk.slice(0, 4) : hs);
  const codeRail = data ? (
    <HeadingGrid
      hs4={railHs4}
      heading_popum_ko={data.heading_popum_ko}
      heading_popum_en={data.heading_popum_en}
      heading_title_ko={data.heading_title_ko}
      heading_title_en={data.heading_title_en}
      subheading_popums_ko={data.subheading_popums_ko}
      subheading_popums_en={data.subheading_popums_en}
      group_popums_ko={data.group_popums_ko}
      group_popums_en={data.group_popums_en}
      rows={data.rows || []}
      national_trees={data.national_trees}
      lang={lang}
      onRowClick={(clickedHsk) =>
        onRowClick
          ? onRowClick(clickedHsk)
          : navigate({ hs: railHs4, hsk: clickedHsk })
      }
      activeHsk={level === "hsk" ? hsk : null}
      compact={true}
      isMobile={isMobile}
      matchTokens={matchTokens}
    />
  ) : (
    <Muted>호 hierarchy 불러오는 중…</Muted>
  );

  if (level === "section") {
    patronCol = <SectionsList activeSection={null} />;
    detailCol = (
      <DetailPlaceholder>
        {isMobile
          ? "부를 선택하면 그 부의 류 목록이 표시됩니다."
          : "← 왼쪽에서 부를 선택하면 그 부의 류 목록이 표시됩니다."}
      </DetailPlaceholder>
    );
  } else if (level === "chapter") {
    // 부 선택 (image 217 + 218) — 좌 patron = 21 부 list (highlight) + 우 detail = 류 popum grid
    patronCol = <SectionsList activeSection={section} />;
    detailCol = <SectionChaptersGrid section={section} />;
  } else if (level === "heading") {
    const sid = sectionIdOfChapter(chapter);
    patronCol = <ChaptersList sectionId={sid} activeChapter={chapter} />;
    // image 211 패턴: 그 류의 모든 호 popum 본문 grid (호 list 짧은 name 만 → 전체 popum)
    detailCol = <ChapterHeadingsGrid chapter={chapter} />;
  } else if (level === "hierarchy") {
    // 호 단계 — 좌측 그 류의 전체 호 목록(선택 호 highlight, 클릭 시 호 전환)
    //   + 우측 그 호의 세번 표(wide). 관세청/CielHS 벤치마크 패턴.
    //   호해설·분류사례는 하단 HeadingTabs 가 계속 표시.
    const chap = hs ? hs.slice(0, 2) : null;
    patronCol = (
      <HeadingsList chapter={chap} activeHs4={hs} onSelect={onHsSelect} />
    );
    if (!data) {
      detailCol = <Muted>호 hierarchy 불러오는 중…</Muted>;
    } else if (invalidSub) {
      detailCol = (
        <div
          role="status"
          style={{
            padding: 18,
            border: "1px solid var(--audit-border-strong)",
            background: "var(--audit-bg-subtle)",
            borderRadius: 6,
            color: "var(--audit-text)",
            fontSize: 13,
            lineHeight: 1.65,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            현행 관세율표(2026년 기준)에 없는 품목번호입니다.
          </div>
          <div>
            <code>{formatTariffLookupCode(sub)}</code>에 해당하는 현행 소호·세번이
            없습니다.
          </div>
          <div style={{ color: "var(--audit-text-muted)", marginTop: 6 }}>
            왼쪽의 제{data.hs4}호를 누르면 이 호의 현행 세분 전체를 확인할 수
            있습니다.
          </div>
        </div>
      );
    } else {
      detailCol = (
        <HeadingGrid
          hs4={data.hs4}
          heading_popum_ko={data.heading_popum_ko}
          heading_popum_en={data.heading_popum_en}
          heading_title_ko={data.heading_title_ko}
          heading_title_en={data.heading_title_en}
          subheading_popums_ko={data.subheading_popums_ko}
          subheading_popums_en={data.subheading_popums_en}
          group_popums_ko={data.group_popums_ko}
          group_popums_en={data.group_popums_en}
          rows={subFilteredRows}
          hideHeadingPopum={!!sub}
          national_trees={data.national_trees}
          lang={lang}
          onRowClick={onRowClick}
          isMobile={isMobile}
          compact={isMobile} // 휴대폰: 9칸 표는 글자가 세로로 쌓인다 — 한글 품명만, 세율·요건은 세번을 눌러 상세에서
          matchTokens={matchTokens}
        />
      );
    }
  } else if (level === "hsk") {
    // 세번 단계 — 좌측 세번 rail(현재 highlight) + 우측 세번 상세(세율·FTA·요건).
    patronCol = codeRail;
    detailCol = <HskDetailPanel hsk={hsk} lang={lang} />;
  }

  // level 별 좌측 patron 폭 — 부/류/호 nav 는 narrow, 세번 단계만 rail 이라 wider.
  const isRail = level === "hsk";
  const patronWidth = isRail ? 380 : 200;
  // 호·세번 단계 patron(호 목록·세번 rail)은 sticky + 자체 스크롤 —
  //   긴 상세(세번 표·세율)를 스크롤해도 좌측 목록이 항상 보임 (관세청식).
  const stickyPatron =
    (level === "hierarchy" || level === "hsk") && !isMobile;

  // 모바일 — "방금 들어간 목록"이 화면 맨 위에 오도록 detail 을 먼저 렌더.
  //   (세로 스택에서 patron(이전/동급 단계 목록)이 위면, 탭해도 아무 일 없어 보임)
  //   chapter=류 목록 / heading=호 목록 / hierarchy=세번 표 / hsk=세번 상세 first.
  const detailFirstOnMobile = isMobile && level !== "section";

  return (
    <div
      style={{
        display: "grid",
        // 모바일 — 세로 스택(표/상세 각각 전체폭). 데스크톱 — 2-컬럼.
        gridTemplateColumns: isMobile ? "1fr" : `${patronWidth}px 1fr`,
        gap: isMobile ? 14 : 12,
        alignItems: "start",
      }}
    >
      {detailFirstOnMobile && <div style={{ minWidth: 0 }}>{detailCol}</div>}
      {stickyPatron ? (
        // 호 목록·세번 rail — sticky + 자체 스크롤. 상세 스크롤 중에도 항상
        //   보이고 목록만 위아래로 훑을 수 있음 (관세청식).
        <StickyPane>{patronCol}</StickyPane>
      ) : (
        patronCol
      )}
      {!detailFirstOnMobile && <div style={{ minWidth: 0 }}>{detailCol}</div>}
    </div>
  );
}

// 호 목록·세번 rail 의 sticky 칸(2026-09-30): 높이를 고정값(100vh-130px)으로 두면 상단 검색바 등으로 시작 위치가 내려갈 때
// 끝 두세 줄이 감싼 스크롤 영역 밖으로 잘렸다(9018.90-8900·9000 이 안 보임). 가장 가까운 스크롤 영역의 실제 높이에 맞춘다.
export function StickyPane({ children, top = 8 }) {
  const ref = useRef(null);
  const [maxH, setMaxH] = useState(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let sc = el.parentElement;
    while (sc && sc !== document.body) {
      const oy = window.getComputedStyle(sc).overflowY;
      if (oy === "auto" || oy === "scroll") break;
      sc = sc.parentElement;
    }
    const measure = () => {
      const h = sc && sc !== document.body ? sc.clientHeight : window.innerHeight;
      if (h > 0) setMaxH(Math.max(240, h - top * 2));
    };
    measure();
    window.addEventListener("resize", measure);
    let ro = null;
    if (sc && sc !== document.body && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(sc);
    }
    return () => {
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, [top]);
  return (
    <div
      ref={ref}
      data-testid="sticky-pane"
      style={{
        position: "sticky",
        top,
        maxHeight: maxH != null ? maxH : "calc(100vh - 130px)",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {children}
    </div>
  );
}
